import { QueryClient,  QueryKey, useInfiniteQuery, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { randomNum, sleep } from "./utils";
import { useApiModeStore } from "../stores/apiMode";
import * as globalApi from "./api";
import * as localApi from "./localDb";
import { useAuth } from "@clerk/clerk-react";

export const queryClient = new QueryClient(
    {
        defaultOptions: {
            queries: {
                gcTime: 0, // Cached data is garbage collected immediately after becoming inactive
            },
        },
    }
)

export type SubscriptionStatus = {
    status: string;
}

// A Person is just a Person
// A Group is a collection of Persons
// Can be a Group but it can be a website or company to something else abstract
// An Event is a ongoing thing. Might involve Persons, Groups, Entities

export type NodeSummary = {
    id: string;
    circleId: string;
    type: 'person' | 'group' | 'entity' | 'effort';
    name: string;
    // A secondary name or title for the node
    subtitle?: string;
    // A short description of the node
    description?: string;
    tags: string[];
    // At least one avatar
    //primaryAvatar: string;
    avatars: string[]; // In the case of Persons it can be one
    lastUpdated?: string;
    timestamp: string;
}


// Also a bad name but ideas for now...
// a note
// a event
// a task
// a set of tasks
export type Knot = {
    id: string;
    circleId: string;
    nodeId: string;
    type: string;
    title: string;
    description: string;
    tags: string[];
    timestamp: string;
}


export const useAddKnot = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const mutationFn = async (data: Knot) => {
        const token = await getToken() ?? '';
        return apiMode === "global" 
            ? globalApi.addKnot(data, token) 
            : localApi.addKnot(data, token);
    };
    return useMutation({ mutationFn });
};

export const useAddNode = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const mutationFn = async (data: NodeSummary) => {
        const token = await getToken() ?? ''
        return apiMode === "global" 
            ? globalApi.addNode(data, token) 
            : localApi.addNode(data, token);
    };
    return useMutation({ mutationFn });
};

export const useEditNode = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const mutationFn = async ({ nodeId, newNode }: { nodeId: string, newNode: NodeSummary }) => {
        const token = await getToken() ?? '';
        return apiMode === "global"
            ? globalApi.editNode(nodeId, token, newNode)
            : localApi.editNode(nodeId, newNode, token);
    };
    return useMutation({ mutationFn });
};

export const useDeleteNode = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const mutationFn = async (id: string) => {
        const token = await getToken() ?? '';
        return apiMode === "global"
            ? globalApi.deleteNode(id, token)
            : localApi.deleteNode(id, token);
    };
    return useMutation({ mutationFn });
};

export const useMergeNodes = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const mutationFn = async (ids: string[]) => {
        const token = await getToken() ?? '';
        return apiMode === "global"
            ? globalApi.mergeNodes(ids, token)
            : localApi.mergeNodes(ids, token);
    };
    return useMutation({ mutationFn });
};

export const useSubscriptionStatus = () => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const queryKey = ['subscriptionStatus'];
    const queryFn = async () => {
        const token = await getToken() ?? '';  
        return apiMode === "global"
            ? globalApi.getSubscriptionStatus()
            : localApi.getSubscriptionStatus(token)
    };
    return chaosUseQuery({ queryKey, queryFn });
}

export const useNodes = ({ searchParam }: { searchParam: string }) => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const queryKey = ['nodes', searchParam];
    const queryFn = async ({ pageParam }: { pageParam: number }) => {
        const token = await getToken() ?? '';  
        return apiMode === "global"
            ? globalApi.getNodes(searchParam, token, pageParam)
            : localApi.getPersons(searchParam, token, pageParam);
    };
    return useInfiniteQuery({
        queryKey,
        queryFn,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
    });
}

export const useKnots = ({ nodeId, searchParam }: { nodeId: string, searchParam: string }) => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const queryKey = ['knots', nodeId, searchParam];
    const queryFn = async ({ pageParam }: { pageParam: number }) => {
        const token = await getToken() ?? '';
        return apiMode === "global"
            ? globalApi.getKnots(nodeId, token, searchParam, pageParam)
            : localApi.getKnots(nodeId, token, searchParam, pageParam);
    };
    return useInfiniteQuery({
        queryKey,
        queryFn,
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
    });
}

export const useNode = (id: string) => {
    const { getToken } = useAuth();
    const { apiMode } = useApiModeStore.getState();
    const queryKey = ['node', id];
    const queryFn = async () => {
        const token = await getToken() ?? '';
        return apiMode === "global"
            ? globalApi.getNode(id, token)
            : localApi.getNode(id, token);
    };
    return chaosUseQuery({ queryKey, queryFn: queryFn });
}

interface ChaosUseQueryParams<TQueryFnData, TError, TData, TQueryKey extends QueryKey> extends UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> {
    queryFn: (...args: unknown[]) => Promise<TQueryFnData>;
}

export const chaosUseQuery = <TQueryFnData, TError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    params: ChaosUseQueryParams<TQueryFnData, TError, TData, TQueryKey>
) => {
    const originalQueryFn = params.queryFn;

    const chaoticQueryFn = async (...args: unknown[]) => {

        await sleep(randomNum(100, 2000));

        if (Math.random() < 0.3) {
            console.log("Random failure triggered by chaosUseQuery")
            throw new Error('Random failure triggered by chaosUseQuery');
        }
        return originalQueryFn(...args);
    };

    return useQuery<TQueryFnData, TError, TData, TQueryKey>({
        ...params,
        queryFn: chaoticQueryFn,
    });
};

const wrapChaosQuery = (queryFn: (...args: unknown[]) => Promise<unknown>) => {
    const chaoticQueryFn = async (...args: unknown[]) => {

        await sleep(randomNum(100, 2000));

        if (Math.random() < 0.3) {
            console.log("Random failure triggered by chaosUseQuery")
            throw new Error('Random failure triggered by chaosUseQuery');
        }
        return queryFn(...args);
    }
    return chaoticQueryFn;
}